/**
 * Content scorer — grade social drafts against the 15-operator
 * best-practices field study (Ask #6).
 *
 * WHY: the Content Engine generates drafts, but operators asked "is this
 * draft any GOOD by the standards that actually move numbers?". This module
 * encodes the nine field-study practices as transparent, deterministic
 * heuristics — a HEURISTIC scorer, deliberately NOT an AI call, so the same
 * text always earns the same score and tests stay stable.
 *
 * PURE by contract: reads only the text passed in. No store, no network,
 * no randomness, no clock. An empty draft honestly scores 0 with
 * suggestions — never a fabricated grade.
 */

/** Platforms with distinct length norms. X favors 40–120 words + punchy one-liners; LinkedIn tolerates longer narrative. */
export type Platform = 'x' | 'linkedin';

/** One graded best-practice axis with its transparent reasoning. */
export interface ContentDimension {
  key: string;
  label: string;
  score: number;
  max: number;
  note: string;
}

/** The full report card for a draft. */
export interface ContentScore {
  /** Normalized 0..100. */
  total: number;
  grade: 'A' | 'B' | 'C' | 'D';
  dimensions: ContentDimension[];
  /** Concrete rewrite tips for the LOW dimensions, in dimension order. */
  suggestions: string[];
}

// ---------------------------------------------------------------------------
// Lexicons — the transparent knobs. Tune here, never mid-heuristic.
// ---------------------------------------------------------------------------

/** Warm-up openers the study flagged as scroll-past signals. */
const GENERIC_OPENERS: RegExp[] = [
  /^some thoughts on/i,
  /^in today['’]?s/i,
  /^excited to/i,
  /^thrilled to/i,
  /^proud to/i,
  /^happy to (announce|share)/i,
  /^i['’]?m (excited|thrilled|proud|pleased)/i,
  /^we['’]?re (excited|thrilled|proud|pleased)/i,
  /^pleased to announce/i,
  /^check out/i,
  /^just wanted to/i,
];

/** Scene/person openers — the study's strongest first-line pattern. */
const SCENE_OPENER = /^(met |i |i['’]m |we |she |he |they |last |yesterday|this morning|today)/i;

/** Hard-sell CTAs that read as naked promo. Phrase-level so "buy ugly" survives. */
const CTA_PHRASES =
  /\b(sign up|buy now|shop now|order now|register (now|here|today)|purchase|subscribe|book a (demo|call)|get started|click (here|the link)|learn more|dm me to buy)\b/i;

const LINK = /https?:\/\/|www\./i;

/** Named roles/ownership words that anchor a story in a findable person. */
const ROLE_WORDS =
  /\b(ceo|cto|cfo|coo|vp|founder|owner|owns|manager|engineer|teacher|nurse|plumber|realtor|landlord|investor|operator|director|dentist|farmer|barber)\b/i;

/** Corporate/AI-polished tone words that erode authenticity. */
const BUZZWORDS = [
  'leverage',
  'synergy',
  'delve',
  'game-changer',
  'game changer',
  'cutting-edge',
  'cutting edge',
  'best-in-class',
  'seamless',
  'unlock',
  'empower',
  'revolutioniz',
  'revolutionar',
  'innovative',
  'disrupt',
  'paradigm',
  'excited to announce',
  'thrilled to announce',
];

const FIRST_PERSON = /\b(i|i['’]m|i['’]ve|my|me|we|we['’]re|our|us)\b/i;

/** First-person past-tense verbs that mark an anecdote (reach content). */
const ANECDOTE_VERBS =
  /\b(met|learned|built|sold|lost|made|started|quit|watched|found|asked|tried|failed)\b/i;

/** References to attached/embedded media. */
const MEDIA_WORDS =
  /\b(photo|image|picture|chart|graph|screenshot|video|clip|infographic|diagram|meme|gif)\b|\[image\]|\[video\]/i;

/** Rewrite tips keyed by dimension. Surfaced for LOW dimensions only. */
const SUGGESTIONS: Record<string, string> = {
  hook: 'Open on a specific person, scene, or number — cut the warm-up line.',
  specificity: 'Add one concrete figure a stranger can locate (a number, $ amount, place, or named role).',
  identity: 'Lead with the story or feeling, not the pitch — save the ask for later.',
  goal: 'Pick ONE goal: a question for replies, a saveable list/anecdote for reach, or one quotable line for likes.',
  format: 'Break into one-idea-per-line with whitespace between thoughts.',
  authenticity: 'Write it like you would say it out loud — first person, no corporate polish words.',
  softSell: 'Move the link to the end — value first, ask last.',
  length: 'Fit the platform: X rewards 40–120 words or a single punchy line; LinkedIn rewards 80–300-word narrative.',
  media: 'Attach a visual (photo, chart, or screenshot) — posts with media travel further.',
};

// ---------------------------------------------------------------------------
// Internal text facts, computed once.
// ---------------------------------------------------------------------------

interface TextFacts {
  raw: string;
  lines: string[]; // non-empty, trimmed
  firstLine: string;
  words: number;
  sentences: number;
  hasLink: boolean;
  hasCTA: boolean;
  empty: boolean;
}

function analyze(text: string): TextFacts {
  const raw = text ?? '';
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const words = raw.split(/\s+/).filter((w) => w.length > 0).length;
  const sentences = raw.split(/[.!?]+/).filter((s) => s.trim().length > 0).length;
  return {
    raw,
    lines,
    firstLine: lines[0] ?? '',
    words,
    sentences,
    hasLink: LINK.test(raw),
    hasCTA: CTA_PHRASES.test(raw),
    empty: lines.length === 0,
  };
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

// ---------------------------------------------------------------------------
// Dimension heuristics — each returns { score, note } against a fixed max.
// ---------------------------------------------------------------------------

function scoreHook(f: TextFacts): ContentDimension {
  const max = 15;
  if (f.empty) return { key: 'hook', label: 'Hook', score: 0, max, note: 'No first line to hook with.' };
  if (GENERIC_OPENERS.some((re) => re.test(f.firstLine))) {
    return { key: 'hook', label: 'Hook', score: 2, max, note: 'Generic warm-up opener — readers scroll past these.' };
  }
  let score = 7;
  const notes: string[] = [];
  if (/[\d$]/.test(f.firstLine)) {
    score += 4;
    notes.push('opens on a number');
  }
  if (f.firstLine.length <= 90) {
    score += 2;
    notes.push('tight first line');
  }
  if (SCENE_OPENER.test(f.firstLine)) {
    score += 2;
    notes.push('opens on a scene/person');
  }
  return {
    key: 'hook',
    label: 'Hook',
    score: clamp(score, 0, max),
    max,
    note: notes.length > 0 ? `Solid opener: ${notes.join(', ')}.` : 'Opener is acceptable but not gripping.',
  };
}

function scoreSpecificity(f: TextFacts): ContentDimension {
  const max = 15;
  if (f.empty) return { key: 'specificity', label: 'Specificity', score: 0, max, note: 'Nothing concrete yet.' };
  let score = 0;
  const anchors: string[] = [];
  if (/\d/.test(f.raw)) {
    score += 7;
    anchors.push('a number');
  }
  if (/[$%]/.test(f.raw)) {
    score += 4;
    anchors.push('a $/% figure');
  }
  if (ROLE_WORDS.test(f.raw)) {
    score += 4;
    anchors.push('a named role');
  }
  return {
    key: 'specificity',
    label: 'Specificity',
    score: clamp(score, 0, max),
    max,
    note: anchors.length > 0 ? `Concrete anchors: ${anchors.join(', ')}.` : 'No anchor a stranger could locate.',
  };
}

function scoreIdentity(f: TextFacts): ContentDimension {
  const max = 10;
  if (f.empty) return { key: 'identity', label: 'Identity over promo', score: 0, max, note: 'Nothing to judge yet.' };
  let score = max;
  const hits: string[] = [];
  if (f.hasCTA) {
    score -= 5;
    hits.push('hard-sell CTA language');
  }
  const firstTwo = f.lines.slice(0, 2).join('\n');
  if (LINK.test(firstTwo)) {
    score -= 5;
    hits.push('leads with a link');
  }
  return {
    key: 'identity',
    label: 'Identity over promo',
    score: clamp(score, 0, max),
    max,
    note: hits.length > 0 ? `Reads as promo: ${hits.join(', ')}.` : 'Leads with identity/emotion, not a pitch.',
  };
}

function scoreGoal(f: TextFacts): ContentDimension {
  const max = 10;
  if (f.empty) return { key: 'goal', label: 'Single goal', score: 0, max, note: 'No goal detectable.' };
  const goals: string[] = [];
  if (/\?/.test(f.raw)) goals.push('replies (asks a question)');
  const listLines = f.lines.filter((l) => /^([-•*]|\d+\.)\s/.test(l)).length;
  if (listLines >= 2 || ANECDOTE_VERBS.test(f.raw)) goals.push('reach (saveable list/anecdote)');
  if (goals.length === 0 && f.sentences <= 2 && !f.hasLink) goals.push('likes (quotable declarative)');
  if (goals.length === 1) {
    return { key: 'goal', label: 'Single goal', score: 10, max, note: `Clear goal: ${goals[0]}.` };
  }
  if (goals.length >= 2) {
    return { key: 'goal', label: 'Single goal', score: 7, max, note: `Mixed goals: ${goals.join(' + ')}.` };
  }
  return { key: 'goal', label: 'Single goal', score: 3, max, note: 'No clear goal — not a question, list, anecdote, or quotable line.' };
}

function scoreFormat(f: TextFacts): ContentDimension {
  const max = 15;
  if (f.empty) return { key: 'format', label: 'Scroll format', score: 0, max, note: 'No text to format.' };
  let score = max;
  const issues: string[] = [];
  const paragraphs = f.raw.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0);
  const densePara = paragraphs.some((p) => {
    const sents = p.split(/[.!?]+/).filter((s) => s.trim().length > 0).length;
    return sents > 3 && !p.includes('\n');
  });
  if (densePara) {
    score -= 6;
    issues.push('a paragraph packs 4+ sentences with no line breaks');
  }
  if (f.lines.some((l) => l.length > 200)) {
    score -= 4;
    issues.push('a single line runs past 200 characters');
  }
  if (paragraphs.length <= 1 && f.raw.length > 280) {
    score -= 3;
    issues.push('no whitespace between thoughts');
  }
  return {
    key: 'format',
    label: 'Scroll format',
    score: clamp(score, 0, max),
    max,
    note: issues.length > 0 ? `Dense: ${issues.join('; ')}.` : 'Short lines and whitespace — scrolls well.',
  };
}

function scoreAuthenticity(f: TextFacts): ContentDimension {
  const max = 10;
  if (f.empty) return { key: 'authenticity', label: 'Authenticity', score: 0, max, note: 'No voice yet.' };
  const lower = f.raw.toLowerCase();
  const found = BUZZWORDS.filter((b) => lower.includes(b));
  let score = 7;
  const notes: string[] = [];
  if (FIRST_PERSON.test(f.raw)) {
    score += 3;
    notes.push('first-person voice');
  } else {
    notes.push('no first-person voice');
  }
  if (found.length > 0) {
    score -= Math.min(8, found.length * 2);
    notes.push(`polish words: ${found.slice(0, 3).join(', ')}`);
  }
  return {
    key: 'authenticity',
    label: 'Authenticity',
    score: clamp(score, 0, max),
    max,
    note: `${notes.join('; ')}.`,
  };
}

function scoreSoftSell(f: TextFacts): ContentDimension {
  const max = 10;
  if (f.empty) return { key: 'softSell', label: 'Soft-sell placement', score: 0, max, note: 'No ask to place.' };
  const hasAsk = f.hasLink || f.hasCTA;
  if (!hasAsk) {
    return { key: 'softSell', label: 'Soft-sell placement', score: max, max, note: 'No link/CTA — nothing misplaced.' };
  }
  const askLine = f.lines.findIndex((l) => LINK.test(l) || CTA_PHRASES.test(l));
  if (f.lines.length <= 2) {
    return { key: 'softSell', label: 'Soft-sell placement', score: 0, max, note: 'All pitch, no story — the ask IS the post.' };
  }
  if (askLine === f.lines.length - 1) {
    return { key: 'softSell', label: 'Soft-sell placement', score: max, max, note: 'Ask sits at the end — value first.' };
  }
  if (askLine >= 0 && askLine < Math.ceil(f.lines.length / 2)) {
    return { key: 'softSell', label: 'Soft-sell placement', score: 2, max, note: 'Ask appears before the value.' };
  }
  return { key: 'softSell', label: 'Soft-sell placement', score: 5, max, note: 'Ask sits mid-post — push it to the last line.' };
}

function scoreLength(f: TextFacts, platform: Platform): ContentDimension {
  const max = 10;
  if (f.empty) return { key: 'length', label: 'Length fit', score: 0, max, note: 'Empty draft.' };
  const w = f.words;
  if (platform === 'linkedin') {
    if (w >= 80 && w <= 300)
      return { key: 'length', label: 'Length fit', score: 10, max, note: `${w} words — in LinkedIn's narrative band (80–300).` };
    if (w >= 40 && w < 80)
      return { key: 'length', label: 'Length fit', score: 7, max, note: `${w} words — a touch short for LinkedIn.` };
    if (w > 300 && w <= 500)
      return { key: 'length', label: 'Length fit', score: 6, max, note: `${w} words — long even for LinkedIn.` };
    if (w > 500) return { key: 'length', label: 'Length fit', score: 2, max, note: `${w} words — far past LinkedIn's tolerance.` };
    return { key: 'length', label: 'Length fit', score: 4, max, note: `${w} words — too thin for LinkedIn narrative.` };
  }
  // X
  if (w >= 40 && w <= 120)
    return { key: 'length', label: 'Length fit', score: 10, max, note: `${w} words — in X's sweet spot (40–120).` };
  if (f.lines.length === 1 && w <= 20 && !f.hasLink && !f.hasCTA)
    return { key: 'length', label: 'Length fit', score: 8, max, note: `${w} words — punchy one-liner, X-friendly.` };
  if ((w >= 20 && w < 40) || (w > 120 && w <= 200))
    return { key: 'length', label: 'Length fit', score: 6, max, note: `${w} words — near X's band, trim or expand.` };
  if (w > 200) return { key: 'length', label: 'Length fit', score: 2, max, note: `${w} words — a thread, not a post.` };
  return { key: 'length', label: 'Length fit', score: 4, max, note: `${w} words — too thin to carry a story.` };
}

function scoreMedia(f: TextFacts): ContentDimension {
  const max = 5;
  if (f.empty) return { key: 'media', label: 'Media', score: 0, max, note: 'No draft, no visual.' };
  if (MEDIA_WORDS.test(f.raw))
    return { key: 'media', label: 'Media', score: max, max, note: 'References a visual — media lifts reach.' };
  return { key: 'media', label: 'Media', score: 2, max, note: 'No visual referenced — a photo/chart would lift this.' };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Grade a draft against the nine field-study practices. Deterministic:
 * same text + platform always yields the identical report. Total is
 * normalized to 0..100 (dimension maxes sum to exactly 100); grade bands
 * A ≥ 80, B ≥ 65, C ≥ 50, D below. Suggestions are rewrite tips for every
 * dimension scoring under 60% of its max, in dimension order.
 */
export function scoreDraft(text: string, opts?: { platform?: Platform }): ContentScore {
  const platform: Platform = opts?.platform ?? 'x';
  const f = analyze(text);

  const dimensions: ContentDimension[] = [
    scoreHook(f),
    scoreSpecificity(f),
    scoreIdentity(f),
    scoreGoal(f),
    scoreFormat(f),
    scoreAuthenticity(f),
    scoreSoftSell(f),
    scoreLength(f, platform),
    scoreMedia(f),
  ];

  const maxSum = dimensions.reduce((n, d) => n + d.max, 0);
  const rawSum = dimensions.reduce((n, d) => n + d.score, 0);
  const total = clamp(Math.round((rawSum / maxSum) * 100), 0, 100);
  const grade: ContentScore['grade'] = total >= 80 ? 'A' : total >= 65 ? 'B' : total >= 50 ? 'C' : 'D';

  const suggestions = dimensions
    .filter((d) => d.score / d.max < 0.6)
    .map((d) => SUGGESTIONS[d.key])
    .filter((s): s is string => Boolean(s));

  return { total, grade, dimensions, suggestions };
}
